import {
  cn,
  Avatar as AvatarBase,
  AvatarImage,
  AvatarFallback,
} from "web-design-system";
import { cva, VariantProps } from "class-variance-authority";
import { useMemo } from "react";

const solidVariants = cva(
  "rounded-lg bg-name-tag-blue text-name-tag-blue-foreground",
  {
    variants: {
      size: {
        default: "w-10 h-10",
        md: "w-9 h-9",
        sm: "w-8 h-8",
        lg: "w-12 h-12",
        xs: "w-7 h-7 rounded-sm",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

const gradientVariants = cva("rounded-lg text-sm", {
  variants: {
    size: {
      default: "w-10 h-10",
      md: "w-9 h-9",
      sm: "w-8 h-8",
      lg: "w-12 h-12",
      xl: "w-16 h-16",
      xs: "w-7 h-7 rounded-sm",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

// CSS variables (not hardcoded hex) so the gradient picks up this app's
// dark-mode overrides for these tokens automatically, with no theme
// detection needed here.
const GRADIENT_COLORS = [
  "var(--name-tag-blue)",
  "var(--name-tag-rose)",
  "var(--name-tag-violet)",
] as const;

// Candidate starting angles for the conic sweep, so different names still
// land on visually distinct rotations even though the gaps between colors
// (360 / GRADIENT_COLORS.length) are always even, whatever the color count.
const GRADIENT_ROTATIONS = [0, 60, 120, 180, 240, 300] as const;

function hashString(value: string) {
  return value.split("").reduce((acc, char, index) => {
    return acc + char.charCodeAt(0) * (index + 1);
  }, 0);
}

// Web CSS supports conic-gradient() natively (unlike react-native-svg,
// which has no conic-gradient primitive and needs a pie-slice/blob
// workaround on mobile) -- use it directly for a smooth angular sweep,
// with the starting angle randomized per name for variety.
function getConicGradient(seed: string) {
  const hash = hashString(seed);
  const rotation = GRADIENT_ROTATIONS[hash % GRADIENT_ROTATIONS.length];
  const colorOffset = hash % GRADIENT_COLORS.length;
  const gapDeg = 360 / GRADIENT_COLORS.length;

  const stops = GRADIENT_COLORS.map((_, index) => {
    const color =
      GRADIENT_COLORS[(index + colorOffset) % GRADIENT_COLORS.length];
    return `${color} ${index * gapDeg}deg`;
  });
  stops.push(`${GRADIENT_COLORS[colorOffset]} 360deg`);

  return `conic-gradient(from ${rotation}deg at 50% 50%, ${stops.join(", ")})`;
}

type AvatarFamilyProps = {
  url?: string;
  fallback: string;
  className?: string;
} & (
  | { variant?: "solid"; size?: VariantProps<typeof solidVariants>["size"] }
  | {
      variant: "gradient";
      size?: VariantProps<typeof gradientVariants>["size"];
    }
);

export function AvatarFamily(props: AvatarFamilyProps) {
  const { url, fallback, className } = props;

  const background = useMemo(
    () =>
      props.variant === "gradient" ? getConicGradient(fallback) : undefined,
    [props.variant, fallback]
  );

  if (props.variant === "gradient") {
    return (
      <AvatarBase
        className={cn(gradientVariants({ size: props.size }), className)}
        style={{ background }}
      >
        <AvatarImage className="object-contain" src={url} />
        <AvatarFallback
          className={cn("font-semibold bg-transparent text-foreground")}
        >
          {fallback}
        </AvatarFallback>
      </AvatarBase>
    );
  }

  return (
    <AvatarBase className={cn(solidVariants({ size: props.size }), className)}>
      <AvatarImage className="object-contain" src={url} />
      <AvatarFallback className="font-semibold bg-transparent">
        {fallback}
      </AvatarFallback>
    </AvatarBase>
  );
}
